// REACT IMPORTS
import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import QuickCheckoutLoginLink from "components/auth/QuickCheckoutLoginLink";

// COMPONENT IMPORTS
import {
  accountAttachGet,
  useAccountDetails,
  userLogin,
} from "store/slices/authSlice";
import { useAppDispatch, useAppSelector } from "store/hooks";
import InputSelect from "components/form/InputSelect";
import InputField from "components/form/InputField";
import Button from "components/form/Button";

// TYPES IMPORTS
import { InfoRedCircleIcon } from "icons/subscription/InfoRedCircleIcon";
import AuthOptions from "./AuthOptions";
import GoogleLoginButton from "./GoogleLoginButton";
import QuestionsFooter from "./QuestionsFooter";
import SupportPhoneLink from "./SupportPhoneLink";
import { usePortalSettingsData } from "store/slices/dashboardSlice";
import {
  decode,
  formatContent,
  replaceMarkers,
  validateEmail,
} from "utils/HelperFunctions";
import { RootState } from "store/store";
import { GOOGLE_CLIENT_ID } from "store/api/constApi";
import { liftLeadingHeading } from "utils/htmlContent";
import { GoogleLoginChallenge, useGoogleAuth } from "hooks/useGoogleAuth";
import { useTid } from "utils/testid";

/* ------------------------------------- LOGIN FORM ------------------------------------- */

const LoginForm = ({ guestLogin, setShowOverlay, onOtpRequired }: any) => {
  const tid = useTid();
  const dispatch = useAppDispatch();
  const { companyMarkers } = useAppSelector(
    (state: RootState) => state.payment
  );
  const accountDetails = useAccountDetails();
  const location = useLocation();
  const [isLoading, setLoading] = useState<boolean>(false);
  const [isErr, setIsErr] = useState<boolean>(false);
  const [isFormatErr, setIsFormatErr] = useState<boolean>(false);
  const [accountId, setAccountId] = useState<boolean>(false);
  const [googleLoginAttemptId, setGoogleLoginAttemptId] = useState<string>("");
  const portalSettings = usePortalSettingsData();
  const [selectAuthField, setSelectAuthField] = useState<string>("");
  const [loginText, setLoginText] = useState<string>("");
  const [selectAdditionalAuthField, setSelectAdditionalAuthField] =
    useState<string>("");

  // Custom fields show the name the cluster gave them in its settings.
  const authOption = (key: string) =>
    AuthOptions(key, accountDetails?.data?.auth_field_labels);

  const { clusterId: cluster_id, id: actionId } = useParams();
  const clusterId = decode(cluster_id || "", true);

  const initialVal = {
    accountid: "",
    email: "",
    phone: "",
    first_name: "",
    last_name: "",
    birthday: "",
    postal_code: "",
    custom1: "",
    custom2: "",
    custom3: "",
    custom4: "",
    custom5: "",
    custom6: "",
    custom7: "",
    custom8: "",
    custom9: "",
    custom10: "",
    custom11: "",
    custom12: "",
    custom13: "",
    custom14: "",
    custom16: "",
    custom17: "",
    custom18: "",
    custom19: "",
    custom20: "",
  };

  const [singleLoginDetails, setSingleLoginDetails] = useState<any>(initialVal);

  useEffect(() => {
    if (isErr) {
      setIsErr(false);
    }
    if (isFormatErr) {
      setIsFormatErr(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectAdditionalAuthField, selectAuthField, singleLoginDetails]);
  const isGoogleAccountSelection = Boolean(googleLoginAttemptId);

  // Keep the verified email visible, but submit only the opaque challenge and
  // selected Account ID back to the Google endpoint.
  const askAccountIdForEmail = ({
    email,
    googleLoginAttemptId: attemptId,
  }: GoogleLoginChallenge) => {
    setSelectAuthField("email");
    setSingleLoginDetails({
      ...initialVal,
      email,
    });
    setGoogleLoginAttemptId(attemptId);
    setAccountId(true);
  };

  useEffect(() => {
    const googleEmail = (location.state as any)?.googleEmail;
    const attemptId = (location.state as any)?.googleLoginAttemptId;
    if (googleEmail && attemptId) {
      askAccountIdForEmail({
        email: googleEmail,
        googleLoginAttemptId: attemptId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { onGoogleCredential, completeGoogleAccountSelection, completeLogin } =
    useGoogleAuth({
      guestLogin,
      setShowOverlay,
      onOtpRequired,
      onStart: () => setLoading(true),
      onFinish: () => setLoading(false),
      onNoMatch: () => setIsErr(true),
      onMultiple: askAccountIdForEmail,
    });

  const loginUser = async (payload: any) => {
    try {
      payload["clusterId"] = clusterId
        ? clusterId
        : actionId
          ? decode(actionId || "").split("_")[1]
          : "";
      const res: any = await dispatch(userLogin(payload)).unwrap();
      if (res && res?.body?.success) {
        completeLogin(res);
        setLoading(false);
      } else {
        setIsErr(true);
      }
    } catch (error) {
      console.log("error =====", error);
    }
  };

  const onSubmit = async (e: any) => {
    e.preventDefault();

    if (isGoogleAccountSelection) {
      const selectedAccountId = singleLoginDetails.accountid.trim();
      if (!selectedAccountId) {
        return;
      }

      await completeGoogleAccountSelection({
        googleLoginAttemptId,
        accountId: selectedAccountId,
      });
      return;
    }

    if (
      singleLoginDetails?.email?.trim() &&
      !validateEmail(singleLoginDetails.email)
    ) {
      setIsFormatErr(true);
      return;
    }

    setLoading(true);
    try {
      let details: any = initialVal;
      Object.keys(singleLoginDetails).forEach(
        (key: string) => (details[key] = singleLoginDetails[key].trim())
      );
      // Customers type phones with dashes, spaces and brackets; only the digits
      // identify the number.
      if (details.phone_number) {
        details.phone_number = details.phone_number.replace(/\D/g, "");
      }
      let payload: any = {
        fields: {
          ...details,
        },
      };
      for (const key in payload?.fields) {
        if (payload?.fields[key] === "") {
          delete payload?.fields[key];
        }
      }
      let cluster_id = clusterId
        ? clusterId
        : actionId
          ? decode(actionId || "").split("_")[1]
          : "";

      let result: any = await dispatch(
        accountAttachGet({ ...payload, clusterId: cluster_id })
      ).unwrap();

      // check account details is attach to multiple or not
      if (result?.body?.success) {
        const { data } = result?.body;
        if (data?.isAttachMultiple === "zero") {
          setIsErr(true);
        } else if (data?.isAttachMultiple === "one") {
          await loginUser(payload);
        } else {
          setAccountId(true);
        }
      } else {
        setIsErr(true);
      }

      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      console.log("Login Err: --->", error?.message);
    }
  };

  const emailFormatAlert = (
    <div
      className="flex items-center gap-[5px] px-[10px] py-3 bg-lightRedBg rounded select-none animate__animated animate__fadeIn"
      role="alert"
      data-testid={tid("email-format-alert")}
    >
      <InfoRedCircleIcon aria-hidden="true" />
      <div className="lg:text-base text-sm text-red leading-[20.83px]">
        Please enter a valid email address.
      </div>
    </div>
  );

  const authFieldOptions = () => {
    let data: any = [];
    accountDetails?.data?.auth_fields?.map((x: string) => {
      if (accountDetails?.data?.auth_type === "single") {
        data?.push({ name: authOption(x)?.label, value: x });
      }
      if (accountDetails?.data?.auth_type === "dual") {
        if (selectAdditionalAuthField) {
          if (selectAdditionalAuthField !== x) {
            data?.push({ name: authOption(x)?.label, value: x });
          }
        } else {
          data?.push({ name: authOption(x)?.label, value: x });
        }
      }
      return x;
    });
    return data;
  };

  const authAdditionalFieldOptions = () => {
    let data: any = [];
    // eslint-disable-next-line array-callback-return
    accountDetails?.data?.auth_fields?.map((x: string) => {
      if (x !== selectAuthField) {
        data?.push({ name: authOption(x)?.label, value: x });
      }
    });
    return data;
  };

  const checkDisable = () => {
    if (isGoogleAccountSelection) {
      return singleLoginDetails.accountid.trim() === "";
    }

    if (accountDetails?.data?.auth_type === "single") {
      if (
        Object.keys(singleLoginDetails).find(
          (x) => singleLoginDetails[x].trim() !== ""
        )
      ) {
        return false;
      } else {
        return true;
      }
    } else if (accountDetails?.data?.auth_type === "dual") {
      let count = 0;
      for (const key in singleLoginDetails) {
        if (singleLoginDetails[key] !== "") {
          count += 1;
          if (count === 2) {
            return false;
          }
        }
      }
      return true;
    } else {
      return false;
    }
  };

  useEffect(() => {
    if (portalSettings && portalSettings?.pp_welcome_msg_value) {
      const sanitizedHtml = replaceMarkers(
        portalSettings.pp_welcome_msg_value,
        companyMarkers
      );

      const formattedHtml = liftLeadingHeading(formatContent(sanitizedHtml));
      setLoginText(formattedHtml);
    }
  }, [portalSettings]);

  return (
    <div
      className="w-full h-full animate__animated animate__fadeIn flex flex-col justify-between items-center animate__animated animate__fadeIn"
      data-testid={tid("form-shell")}
    >
      <div className="w-full lg:min-w-[430px]">
        {portalSettings &&
        ["C", "D"].includes(portalSettings?.pp_welcome_msg_flag) &&
        portalSettings?.pp_welcome_msg_value ? (
          <div
            id="welcome-content"
            className="welcome-message max-w-[520px] pb-10"
            style={{ wordBreak: "keep-all", overflowWrap: "normal" }}
            dangerouslySetInnerHTML={{ __html: loginText }}
          ></div>
        ) : (
          <>
            <div className="lg:text-3xl text-xl font-bold -tracking-[0.6px] leading-normal text-txtPrimary">
              Welcome - Secure Payment Portal
            </div>
            <p className="lg:py-10 pt-6 pb-10 max-w-[520px] text-txtParagraph lg:text-base text-sm leading-normal font-normal">
              Please have your account information ready for identification
              purposes. If you are having trouble logging in, please contact our
              representative at{" "}
              <SupportPhoneLink
                phone={companyMarkers?.["Support Phone"]}
                className="underline"
              />
              .
            </p>
          </>
        )}
        <form
          onSubmit={onSubmit}
          noValidate
          data-testid={tid("credential-login-form")}
        >
          <div className="max-w-[520px] flex flex-col gap-10 lg:gap-[60px]">
            <div className="w-full flex flex-col gap-6">
              {isGoogleAccountSelection ? (
                <InputField
                  showLabel={true}
                  type="email"
                  name="email"
                  label="Email"
                  data-testid={tid("primary-auth-value-input")}
                  aria-label="Google verified email"
                  value={singleLoginDetails.email}
                  disabled={true}
                  _className="!font-medium !text-base"
                />
              ) : accountDetails ? (
                accountDetails?.data?.auth_type === "single" ? (
                  <div className="flex flex-col gap-6">
                    {accountDetails?.data?.auth_fields?.length > 0 ? (
                      <InputSelect
                        label="SECURE LOGIN WITH"
                        placeholder="Select authentication method"
                        options={authFieldOptions()}
                        selected={selectAuthField}
                        data-testid={tid("primary-auth-method-select")}
                        aria-label="Primary authentication method"
                        setSelected={(e: any) => {
                          setSelectAuthField(e?.value);
                          setSingleLoginDetails(initialVal);
                        }}
                        _className="!font-medium !text-base"
                      />
                    ) : null}
                    {accountDetails?.data?.auth_fields?.length > 0 &&
                    selectAuthField &&
                    authOption(selectAuthField) ? (
                      <div className="flex flex-col gap-3">
                        <InputField
                          showLabel={true}
                          type={authOption(selectAuthField)?.type || "text"}
                          name={authOption(selectAuthField)?.key || ""}
                          label={authOption(selectAuthField)?.label}
                          data-testid={tid("primary-auth-value-input")}
                          aria-label={`Enter ${
                            authOption(selectAuthField)?.label ||
                            "primary authentication value"
                          }`}
                          placeholder={
                            authOption(selectAuthField)?.placeHolder ||
                            "Enter the value"
                          }
                          value={singleLoginDetails?.[selectAuthField] ?? ""}
                          onChange={(e: any) => {
                            setSingleLoginDetails({
                              ...singleLoginDetails,
                              [selectAuthField]: e?.target?.value,
                            });
                          }}
                          _className="!font-medium !text-base"
                        />
                        {isFormatErr &&
                          selectAuthField === "email" &&
                          emailFormatAlert}
                      </div>
                    ) : null}
                  </div>
                ) : (
                  accountDetails?.data?.auth_type === "dual" && (
                    <div className="flex flex-col gap-10">
                      <div className="flex flex-col gap-6">
                        {accountDetails?.data?.auth_fields?.length > 1 ? (
                          <InputSelect
                            label="SECURE LOGIN WITH"
                            placeholder="Select authentication method"
                            options={authFieldOptions()}
                            selected={selectAuthField}
                            data-testid={tid("primary-auth-method-select")}
                            aria-label="Primary authentication method"
                            setSelected={(e: any) => {
                              setSelectAuthField(e?.value);
                              // setSelectAdditionalAuthField("");
                              setSingleLoginDetails((state: any) => ({
                                ...state,
                                [selectAuthField]: "",
                              }));
                            }}
                            _className="!font-medium !text-base"
                          />
                        ) : null}
                        {accountDetails?.data?.auth_fields?.length > 1 &&
                        selectAuthField &&
                        authOption(selectAuthField) ? (
                          <div className="flex flex-col gap-3">
                            <InputField
                              showLabel={true}
                              type={authOption(selectAuthField)?.type || "text"}
                              name={authOption(selectAuthField)?.key || ""}
                              label={authOption(selectAuthField)?.label || ""}
                              data-testid={tid("primary-auth-value-input")}
                              aria-label={`Enter ${
                                authOption(selectAuthField)?.label ||
                                "primary authentication value"
                              }`}
                              placeholder={
                                authOption(selectAuthField)?.placeHolder ||
                                "Enter the value"
                              }
                              value={
                                singleLoginDetails?.[selectAuthField] ?? ""
                              }
                              onChange={(e: any) => {
                                setSingleLoginDetails({
                                  ...singleLoginDetails,
                                  [selectAuthField]: e?.target?.value,
                                });
                              }}
                              _className="!font-medium !text-base"
                            />
                            {isFormatErr &&
                              selectAuthField === "email" &&
                              emailFormatAlert}
                          </div>
                        ) : null}
                      </div>
                      <div className="flex items-center gap-[6px]">
                        <div className="h-[1px] w-full bg-strokePrimary" />
                        <div className="flex whitespace-nowrap text-sm text-txtSecondary font-normal leading-normal">
                          Additional Auth Method
                        </div>
                        <div className="h-[1px] w-full bg-strokePrimary" />
                      </div>
                      <div className="flex flex-col gap-6">
                        {accountDetails?.data?.auth_fields?.length > 1 ? (
                          <InputSelect
                            showLabel={false}
                            label="SECURE LOGIN WITH"
                            placeholder="Select authentication method"
                            options={authAdditionalFieldOptions()}
                            selected={selectAdditionalAuthField}
                            data-testid={tid("additional-auth-method-select")}
                            aria-label="Additional authentication method"
                            setSelected={(e: any) => {
                              setSingleLoginDetails((state: any) => ({
                                ...state,
                                [selectAdditionalAuthField]: "",
                              }));
                              setSelectAdditionalAuthField(e?.value);
                            }}
                            _className="!font-medium !text-base"
                          />
                        ) : null}
                        {accountDetails?.data?.auth_fields?.length > 1 &&
                        selectAdditionalAuthField &&
                        authOption(selectAdditionalAuthField) ? (
                          <div className="flex flex-col gap-3">
                            <InputField
                              showLabel={true}
                              type={
                                authOption(selectAdditionalAuthField)?.type ||
                                "text"
                              }
                              name={
                                authOption(selectAdditionalAuthField)?.key || ""
                              }
                              label={
                                authOption(selectAdditionalAuthField)?.label ||
                                ""
                              }
                              data-testid={tid("additional-auth-value-input")}
                              aria-label={`Enter ${
                                authOption(selectAdditionalAuthField)?.label ||
                                "additional authentication value"
                              }`}
                              placeholder={
                                authOption(selectAdditionalAuthField)
                                  ?.placeHolder || "Enter the value"
                              }
                              value={
                                singleLoginDetails?.[
                                  selectAdditionalAuthField
                                ] ?? ""
                              }
                              onChange={(e: any) => {
                                setSingleLoginDetails({
                                  ...singleLoginDetails,
                                  [selectAdditionalAuthField]: e?.target?.value,
                                });
                              }}
                              _className="!font-medium !text-base"
                            />
                            {isFormatErr &&
                              selectAdditionalAuthField === "email" &&
                              emailFormatAlert}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  )
                )
              ) : null}

              {accountId ? (
                <InputField
                  showLabel={true}
                  type={"text"}
                  name={"accountid"}
                  label={"Account ID"}
                  data-testid={tid("account-id-input")}
                  aria-label="Enter account ID"
                  placeholder={"Enter Account ID"}
                  value={singleLoginDetails?.accountid ?? ""}
                  onChange={(e: any) => {
                    setSingleLoginDetails({
                      ...singleLoginDetails,
                      ["accountid"]: e?.target?.value,
                    });
                  }}
                  _className="!font-medium !text-base"
                />
              ) : null}
            </div>
            <div className="w-full">
              <Button
                className="w-full"
                text="Login"
                data-testid={tid("login-submit-button")}
                aria-label="Log in"
                isDisabled={checkDisable()}
                isLoading={isLoading}
                type="submit"
                variant="variable"
              />
              {isErr && (
                <div
                  className="flex items-center gap-[5px] mt-3 px-[10px] py-3 bg-lightRedBg rounded select-none animate__animated animate__fadeIn"
                  role="alert"
                  data-testid={tid("login-error-alert")}
                >
                  <InfoRedCircleIcon aria-hidden="true" />
                  <div className="lg:text-base text-xs text-red leading-[20.83px]">
                    Login failed. Please check your credentials and try again.
                  </div>
                </div>
              )}
              {GOOGLE_CLIENT_ID &&
              !isGoogleAccountSelection &&
              accountDetails?.data?.auth_fields?.includes("email") ? (
                <div className="flex flex-col gap-3 mt-4">
                  <div className="flex items-center gap-[6px]">
                    <div className="h-[1px] w-full bg-strokePrimary" />
                    <div className="flex whitespace-nowrap text-sm text-txtSecondary font-normal leading-normal">
                      or
                    </div>
                    <div className="h-[1px] w-full bg-strokePrimary" />
                  </div>
                  <GoogleLoginButton
                    onCredential={onGoogleCredential}
                    data-testid={tid("google-login-button")}
                  />
                </div>
              ) : null}
            </div>
          </div>
        </form>
        {!isErr &&
          accountDetails?.data &&
          (accountDetails?.data?.login_type === "BOTH" ||
            accountDetails?.data?.login_type === "EP") && (
            <div className="max-w-[520px] flex flex-col gap-4 items-center pt-4">
              <Link
                to={`/${cluster_id}/login-via-cred`}
                className="lg:text-base text-sm font-medium text-blue hover:!text-btnPrimaryHover leading-normal cursor-pointer"
                data-testid={tid("use-password-login-link")}
                aria-label="Use your password instead"
              >
                Use your password instead
              </Link>
            </div>
          )}
        <div className="max-w-[520px] w-full pt-4">
          <QuickCheckoutLoginLink
            guestLogin={guestLogin}
            testId={tid("quick-checkout-link")}
          />
        </div>
      </div>
      {/* lg:w-full so the footer starts at the form's left edge on desktop, as in
          the design, instead of shrink-wrapping and centring under it. */}
      <div className={`pb-5 lg:pt-0 pt-4 lg:w-full`}>
        <QuestionsFooter />
      </div>
    </div>
  );
};

export default LoginForm;
